import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CalendarSync, Copy, RotateCw } from "lucide-react";
import { toast } from "sonner";

/**
 * Pro/Premium-only iCal feed panel for Settings.
 *
 * Shows the unique feed URL (lazily minted on first reveal), a Copy
 * button, and a Regenerate button that rotates the token — which
 * immediately invalidates any previously-shared URL.
 *
 * For Basic plans the panel is rendered but disabled, with a small
 * upgrade hint. We avoid hiding it entirely so Basic-Ledere can SEE the
 * feature exists, which is also what the price-side promises.
 */
export function IcalFeedPanel({ plan }) {
  const isAllowed = plan === "pro" || plan === "premium" || plan === "demo";
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!isAllowed) return;
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/business/ical-feed");
        if (!alive) return;
        setUrl(res.data?.url || "");
      } catch {
        // 402 or any error — leave url empty; the disabled UI handles it.
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [isAllowed]);

  const copy = () => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success("Lenken er kopiert");
  };

  const rotate = async () => {
    if (!isAllowed) return;
    if (!window.confirm("Generere ny feed-lenke? Den gamle lenken slutter å virke umiddelbart.")) return;
    setBusy(true);
    try {
      const res = await api.post("/business/ical-feed/rotate");
      setUrl(res.data?.url || "");
      toast.success("Ny lenke generert");
    } catch {
      toast.error("Kunne ikke generere ny lenke");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="ical-feed-section" className="space-y-2">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <Label className="flex items-center gap-1.5">
          <CalendarSync className="w-4 h-4 text-primary" strokeWidth={1.75} />
          Kalenderfeed (iCal)
        </Label>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-primary">
          Pro · Premium
        </span>
      </div>
      <Input
        readOnly
        type="text"
        value={isAllowed && url ? url : ""}
        placeholder={isAllowed ? "Henter lenke…" : "Tilgjengelig i Pro og Premium"}
        className={`rounded-xl font-mono text-xs ${!isAllowed || !url ? "opacity-60" : ""}`}
        onFocus={(e) => e.target.select()}
        data-testid="ical-feed-url-input"
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-xl"
          onClick={copy}
          disabled={!isAllowed || !url}
          data-testid="ical-feed-copy-btn"
        >
          <Copy className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
          Kopier lenke
        </Button>
        <Button
          type="button"
          variant="outline"
          className="rounded-xl"
          onClick={rotate}
          disabled={!isAllowed || busy || !loaded}
          data-testid="ical-feed-rotate-btn"
        >
          <RotateCw className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
          {busy ? "Genererer…" : "Generer ny"}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground leading-snug">
        {isAllowed
          ? "Lim inn lenken i Apple Kalender, Google Kalender eller Outlook for å vise kommende bookinger. Lenken er privat — del kun med personer som skal se bookingene."
          : "Få bookingene dine direkte i Apple eller Google Kalender. Tilgjengelig i Pro og Premium."}
      </p>
    </div>
  );
}
